import { LANE_COLORS, cssColor } from '../render/colors';
import type { RaceSim } from '../sim/race/race-sim';
import type { RaceEvent } from '../sim/race/types';
import { el } from './dom';

function ordinal(place: number): string {
  const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
  return `${place}${suffix}`;
}

/** Race-day DOM overlay: the stamina bar is the co-star of the show. */
export class RaceHud {
  private readonly wrap: HTMLElement;
  private readonly staminaFill: HTMLElement;
  private readonly staminaWrap: HTMLElement;
  private readonly speedText: HTMLElement;
  private readonly countdown: HTMLElement;
  private readonly flash: HTMLElement;
  private readonly commentary: HTMLElement;
  private readonly dots: HTMLElement[] = [];
  private lastTextKey = '';

  constructor(root: HTMLElement, laneCount: number) {
    this.staminaFill = el('div', { class: 'stamina-fill' });
    this.staminaWrap = el('div', { class: 'stamina-wrap' }, this.staminaFill);
    this.speedText = el('div', { class: 'race-speed' });
    this.countdown = el('div', { class: 'race-countdown' });
    this.flash = el('div', { class: 'race-flash' });
    this.commentary = el('div', { class: 'race-commentary' });

    const strip = el('div', { class: 'progress-strip' });
    for (let lane = 0; lane < laneCount; lane += 1) {
      const dot = el('div', { class: lane === 0 ? 'progress-dot you' : 'progress-dot' });
      dot.style.background = cssColor(LANE_COLORS[lane] ?? 0xffffff);
      strip.append(dot);
      this.dots.push(dot);
    }

    this.wrap = el(
      'div',
      { class: 'race-hud' },
      strip,
      this.countdown,
      this.flash,
      this.commentary,
      el(
        'div',
        { class: 'race-bottom' },
        el('div', { class: 'stamina-label' }, 'STAMINA'),
        this.staminaWrap,
        this.speedText,
      ),
    );
    root.append(this.wrap);
  }

  handleEvents(events: RaceEvent[]): void {
    for (const event of events) {
      switch (event.kind) {
        case 'gun':
          this.showFlash('GO!', 'go');
          break;
        case 'start':
          if (event.lane === 0) {
            if (event.quality === 'perfect') this.showFlash('PERFECT BREAK!', 'good');
            if (event.quality === 'flinch') this.showFlash('FLINCH!', 'bad');
          }
          break;
        case 'exhausted':
          if (event.lane === 0) this.showFlash('BLOWN!', 'bad');
          break;
        case 'recovered':
          if (event.lane === 0) this.showFlash('BACK IN STRIDE', 'good');
          break;
        case 'finish':
          if (event.lane === 0) this.showFlash(`${ordinal(event.place)} — ${event.timeS.toFixed(2)}s`, event.place === 1 ? 'good' : 'go');
          break;
        default:
          break;
      }
    }
  }

  update(sim: RaceSim): void {
    const state = sim.state;
    const player = state.lanes[0]!;
    const params = sim.setup.player;

    const fraction = Math.max(0, Math.min(1, player.stamina / params.staminaMax));
    this.staminaFill.style.width = `${(fraction * 100).toFixed(1)}%`;
    this.staminaWrap.classList.toggle('blown', player.exhausted);
    this.staminaWrap.classList.toggle('low', !player.exhausted && fraction < 0.3);

    state.lanes.forEach((lane, index) => {
      const dot = this.dots[index];
      if (!dot) return;
      const progress = Math.max(0, Math.min(1, lane.x / sim.setup.distanceM));
      dot.style.left = `${(progress * 100).toFixed(1)}%`;
    });

    let countdownText = '';
    if (state.phase === 'countdown') {
      const secondsLeft = Math.ceil((state.gunTick - state.tick) * sim.setup.rules.tickS);
      countdownText = secondsLeft > 0 ? String(secondsLeft) : '';
    }
    const textKey = `${countdownText}|${player.v.toFixed(1)}`;
    if (textKey !== this.lastTextKey) {
      this.lastTextKey = textKey;
      this.countdown.textContent = countdownText;
      this.speedText.textContent = `${player.v.toFixed(1)} m/s`;
    }
  }

  setCommentary(line: string): void {
    this.commentary.textContent = `📢 ${line}`;
    this.commentary.className = 'race-commentary';
    void this.commentary.offsetWidth;
    this.commentary.className = 'race-commentary show';
  }

  reset(): void {
    this.lastTextKey = '';
    this.flash.className = 'race-flash';
    this.flash.textContent = '';
    this.commentary.textContent = '';
    this.commentary.className = 'race-commentary';
  }

  private showFlash(text: string, tone: 'go' | 'good' | 'bad'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash';
    // Force a reflow so the animation restarts even on repeat tones.
    void this.flash.offsetWidth;
    this.flash.className = `race-flash show ${tone}`;
  }

  dispose(): void {
    this.wrap.remove();
  }
}
